import { Question } from '@phosphor-icons/react';
import { useState } from 'react';
import { useWatch, type Control, type RegisterOptions } from 'react-hook-form';
import { REGEX } from '../../../shared/constants/regex';
import type { ParsedFullName } from '../../../shared/utils/parseFullName.util';
import Input from '../../../components/atoms/Input';
import RfcPrefillHint from '../../../components/molecules/RfcPrefillHint';
import type { RfcPrefill } from '../../../shared/fiscal/lib/rfcPrefill';
import RfcHelpDrawer from './LinkingFlowStep2/RfcHelpDrawer';
import type { RfcHelpEntryPoint } from './orchestration/useLinkingTracking';

export type SATCredentialsFormValues = {
	rfc: string;
	password: string;
};

// Accept any react-hook-form Control whose value shape is a superset of the
// `rfc` / `password` fields the form drives. LinkingFlowStep2's form, for
// example, also has efirma keys we don't touch here.
type SATCredentialsFormProps = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	control: Control<any>;
	/** RFC con el que useCredentialsForm prellenó el campo. Pinta la leyenda
	 * («Verificado con el SAT» o «Lo calculamos con tu CURP») mientras el campo
	 * lo conserve. */
	rfcPrefill?: RfcPrefill | null;
	/** When `'auto'` (default) the password field appears once the RFC has
	 * the right format. Use `'always'` or `'hidden'` to force the visibility. */
	passwordVisibility?: 'auto' | 'always' | 'hidden';
	/** Marks the password as optional in the label and skips required/min
	 * validation rules. */
	passwordOptional?: boolean;
	/** Helper copy rendered below the password label. */
	passwordHint?: string;
	/** Override the default react-hook-form rules on each input. */
	rfcRules?: RegisterOptions;
	passwordRules?: RegisterOptions;
	/** Initial values for the "¿Cómo genero mi RFC?" drawer. */
	initialCurp?: string;
	initialFullName?: string;
	onRfcGenerated?: (result: {
		rfc: string;
		curp: string;
		name: ParsedFullName;
	}) => void;
	/** When provided, renders the "¿Cómo recupero mi contraseña del SAT?"
	 * help button beside the password field. Parent owns whatever modal opens. */
	onPasswordRecoveryClick?: () => void;
	/** Error de credenciales del submit (p.ej. contraseña incorrecta). Se pinta
	 * PEGADO al input que lo causó y sin fondo: un banner suelto abajo del
	 * formulario obliga a adivinar cuál de los dos campos está mal. */
	passwordError?: string;
	/** Click en ese error — el parent abre su superficie de ayuda. */
	onPasswordErrorClick?: () => void;
	/** Modo controlado del drawer «Generar mi RFC»: el parent lo abre desde
	 * fuera del formulario (la fila primera del modal de ayuda). Si no se pasan,
	 * el drawer vive con estado propio y solo lo abre el «?» del input. */
	rfcHelpOpen?: boolean;
	rfcHelpEntryPoint?: RfcHelpEntryPoint;
	onRfcHelpOpenChange?: (open: boolean, entryPoint?: RfcHelpEntryPoint) => void;
};

// RFC: 12 (moral) o 13 (persona física) caracteres, en mayúsculas y sin
// espacios. REGEX.rfcRegex valida la estructura completa (iniciales + fecha
// válida + homoclave). Sanitizamos el input para que nunca llegue con espacios
// ni minúsculas.
const sanitizeRfc = (value: string) =>
	value.toUpperCase().replace(/[^A-ZÑ&0-9]/g, '');

// La contraseña del SAT (CIEC) no contiene espacios; los quitamos para evitar
// que un espacio pegado por accidente cause un fallo de vinculación.
const sanitizePassword = (value: string) => value.replace(/\s+/g, '');

const DEFAULT_RFC_RULES: RegisterOptions = {
	required: 'El RFC es requerido',
	minLength: {
		value: 12,
		message: 'El RFC debe tener 12 o 13 caracteres',
	},
	maxLength: {
		value: 13,
		message: 'El RFC debe tener 12 o 13 caracteres',
	},
	pattern: {
		value: REGEX.rfcRegex,
		message: 'Revisa tu RFC: el formato no es válido (ej. VECJ920101AB1).',
	},
};

const DEFAULT_PASSWORD_RULES: RegisterOptions = {
	required: 'La contraseña es requerida',
	minLength: {
		value: 8,
		message: 'La contraseña del SAT tiene al menos 8 caracteres',
	},
};

/** Botón de ayuda de un campo: se para a la derecha de su input, con la misma
 * altura y el mismo redondeo, para que lea como parte del campo y no como un
 * adorno suelto. */
const FieldHelpButton = ({
	onClick,
	label,
}: {
	onClick: () => void;
	label: string;
}) => (
	<button
		type='button'
		onClick={onClick}
		aria-label={label}
		style={{ borderRadius: '10px' }}
		className='bg-h-primary-90 text-h-primary-50 flex h-10 w-10 shrink-0 items-center justify-center self-end'>
		<Question size={20} weight='fill' />
	</button>
);

const SATCredentialsForm = ({
	control,
	rfcPrefill,
	passwordVisibility = 'auto',
	passwordOptional = false,
	passwordHint,
	rfcRules,
	passwordRules,
	initialCurp,
	initialFullName,
	onRfcGenerated,
	onPasswordRecoveryClick,
	passwordError,
	onPasswordErrorClick,
	rfcHelpOpen,
	rfcHelpEntryPoint,
	onRfcHelpOpenChange,
}: SATCredentialsFormProps) => {
	const [internalRfcHelpOpen, setInternalRfcHelpOpen] = useState(false);
	const isRfcHelpControlled = rfcHelpOpen !== undefined;
	const showRfcHelpDrawer = isRfcHelpControlled
		? rfcHelpOpen
		: internalRfcHelpOpen;
	const setShowRfcHelpDrawer = (
		open: boolean,
		entryPoint: RfcHelpEntryPoint = 'field_help',
	) => {
		if (isRfcHelpControlled) onRfcHelpOpenChange?.(open, entryPoint);
		else setInternalRfcHelpOpen(open);
	};

	const rfcValueRaw = useWatch({ control, name: 'rfc' }) ?? '';
	const rfcValue =
		typeof rfcValueRaw === 'string' ? rfcValueRaw.toUpperCase() : '';
	const rfcFormatValid = REGEX.rfcRegex.test(rfcValue);

	const canShowPassword =
		passwordVisibility === 'always'
			? true
			: passwordVisibility === 'hidden'
				? false
				: rfcFormatValid;

	const effectiveRfcRules = rfcRules ?? DEFAULT_RFC_RULES;
	const effectivePasswordRules =
		passwordRules ?? (passwordOptional ? undefined : DEFAULT_PASSWORD_RULES);

	const passwordLabel = passwordOptional
		? 'Contraseña del SAT (opcional)'
		: 'Contraseña del SAT';

	const handleRfcGenerated = (result: {
		rfc: string;
		curp: string;
		name: ParsedFullName;
	}) => {
		onRfcGenerated?.(result);
		setShowRfcHelpDrawer(false);
	};

	return (
		<div className='flex flex-col gap-2'>
			<Input
				name='rfc'
				type='text'
				label='RFC'
				placeholder='Ingresa tu RFC'
				control={control}
				rules={effectiveRfcRules}
				maxLength={13}
				showValueUpperCase
				transform={sanitizeRfc}
				fieldAction={
					<FieldHelpButton
						onClick={() => setShowRfcHelpDrawer(true, 'field_help')}
						label='¿Cómo genero mi RFC?'
					/>
				}
			/>

			<RfcPrefillHint
				prefilledRfc={rfcPrefill?.rfc ?? ''}
				status={rfcPrefill?.status ?? ''}
				value={rfcValue}
			/>

			{canShowPassword && (
				<>
					<Input
						name='password'
						type='password'
						label={passwordLabel}
						placeholder='Ingresa tu contraseña del portal del SAT'
						control={control}
						rules={effectivePasswordRules}
						transform={sanitizePassword}
						fieldAction={
							onPasswordRecoveryClick && (
								<FieldHelpButton
									onClick={onPasswordRecoveryClick}
									label='¿Cómo recupero mi contraseña del SAT?'
								/>
							)
						}
					/>

					{passwordError &&
						(onPasswordErrorClick ? (
							<button
								type='button'
								onClick={onPasswordErrorClick}
								className='text-h-error-50 -mt-1 text-left text-sm leading-snug font-medium'>
								{passwordError}
							</button>
						) : (
							<p className='text-h-error-50 -mt-1 text-sm leading-snug font-medium'>
								{passwordError}
							</p>
						))}

					{passwordHint && (
						<p className='text-h-text-light -mt-1 text-xs leading-relaxed'>
							{passwordHint}
						</p>
					)}

				</>
			)}

			<RfcHelpDrawer
				open={showRfcHelpDrawer}
				onOpenChange={(open) => setShowRfcHelpDrawer(open)}
				entryPoint={isRfcHelpControlled ? rfcHelpEntryPoint : 'field_help'}
				initialCurp={initialCurp}
				initialFullName={initialFullName}
				onRfcGenerated={handleRfcGenerated}
			/>
		</div>
	);
};

export default SATCredentialsForm;
